import { makeAutoObservable, toJS } from 'mobx'
import { cartMobx, restaurantConfig, productMobx, restaurantCenter, RESTAURANT_EVENT, Console, storeMobx } from '@mobx/mall'
import { mapValues, groupBy, sumBy, merge, pick, values, cloneDeep } from '@dt/fe-utils/dist/lodash'
import { toDecimal } from '@dt/fe-utils'
import FeeAPI from 'api/fee.api'

const console = new Console('console.fee')

type TCalcTaxAndServiceDiningOption = 'dineIn' | 'toGo' | 'delivery'
interface ICalcTaxAndServiceChargeData {
  cityId: number;
  person: number;
  detail: {
    productNo: string;
    type: string;
    originalPrice: number;
    paidPrice: number;
    pk: string;
    taxCategoryId: number;
    taxInPrice: boolean;
  }[];
  freeTaxList: number[];
}
/** 服务费计算结果 */
interface ICalculatedServiceCharge {
  id: number;
  name: string;
  /** 不知道什么意思 */
  discounted: string;
  diningOption: string;
  type: 'person' | 'amount' | 'percent';
  /** 人数计算条件 */
  applyRange: 1 | null;
  /** 计算条件的值 */
  value: number;
  /** 服务费计算的结果 */
  amount: number;
  /** 服务费含税 */
  tax: {
    name: string;
    category: string;
    rate: number;
    amount: number;
  };
}

class Fee {
  constructor () {
    makeAutoObservable(this, {
      taxMapConfig: false
    })
    // 费用的变化更新事件
    restaurantCenter.on(RESTAURANT_EVENT.FEE_CONFIG_UPDATED, () => {
      this.calcFee()
    })
    restaurantCenter.on(RESTAURANT_EVENT.MENU_END, () => {
      this.calcFee()
    })
  }
  /** 税的配置树，当前跟sdk一致，需要统一typing */
  taxMapConfig: ITaxMapConfig = {}
  /** 服务费配置 */
  gratuityConfig: IGratuityConfig = {
    name: ''
  }
  /** 税 */
  calculatedTax: ICalculatedTax[] = [];
  /** 税总 */
  calculatedTaxTotal = 0;
  /** 税总 */
  calculatedFreeTaxTotal = 0;
  /** 含税总 */
  calculatedShowTaxTotal = 0;
  /** 服务费 */
  calculatedServiceCharge: ICalculatedServiceCharge[] = [];
  /** 服务费总 */
  calculatedServiceChargeTotal = 0;
  /** 是否免税的action */
  taxFreeAction: {
    [pk: string]: boolean;
  } = {}

  get pkToTax (): { [pk: string]: number } {
    // 不需要含税的金额
    return mapValues(groupBy(this.calculatedTax.filter(taxItem => !taxItem.taxInPrice), 'pk'), (taxItems) => sumBy(taxItems, 'amount'))
  }

  get pkToFreeTax (): { [pk: string]: number } {
    // 不需要含税的金额
    return mapValues(groupBy(this.calculatedTax.filter(taxItem => !taxItem.taxInPrice), 'pk'), (taxItems) => sumBy(taxItems, 'amount'))
  }

  // 含税价的金额map
  get pkToShowTax (): { [pk: string]: number } {
    return mapValues(groupBy(this.calculatedTax.filter(taxItem => taxItem.taxInPrice), 'pk'), (taxItems) => sumBy(taxItems, 'amount'))
  }

  /** 置入税和服务费 */
  setFee (data: any) {
    this.calculatedTax = data.taxes.concat(data.gratuityTaxes || [])
    this.calculatedFreeTaxTotal = toDecimal(data.freeTaxTotal)
    this.calculatedServiceCharge = data.gratuities
    this.calculatedTaxTotal = toDecimal(data.taxTotal - data.showTaxTotal)
    this.calculatedShowTaxTotal = toDecimal(data.showTaxTotal)
    this.calculatedServiceChargeTotal = data.gratuityTotal
  }

  /** 计算税和服务费 */
  calcFee () {
    if (bo === 'cn') {
      return
    }
    // const diningOption = cartMobx.info.eatType as TCalcTaxAndServiceDiningOption
    const items = cloneDeep(cartMobx.availableItems)
    const detail = items.map((item) => {
      const taxCategoryId = (productMobx.noToViewProductMap[item.productNo])?.taxCategoryId || 0
      const taxInPrice = (productMobx.noToViewProductMap[item.productNo])?.taxInPrice || false
      // if (!taxCategoryId) {
      //   console.error('餐品' + item.name + '找不到税配置')
      // }
      return {
        pk: item.pk,
        productNo: item.productNo,
        type: 'mallProduct',
        // diningOption,
        isTaxFree: item.isTaxFree,
        originalPrice: item.totalPrice,
        paidPrice: item.realPrice,
        taxCategoryId,
        taxInPrice,
        cnt: item.cnt
      }
    })
    // 如果无餐品 则不再调用计算
    if (!detail.length) {
      this.clearFee()
    } else if (!restaurantConfig.fee) {
      console.error('🍄计算税和服务费未加载')
    } else if (!storeMobx.info.cityId) {
      console.error('🍄门店city未设置，所以无法使用计算')
    } else {
      const calcData: ICalcTaxAndServiceChargeData = {
        cityId: storeMobx.info.cityId,
        person: cartMobx.info.person,
        // diningOption,
        detail,
        freeTaxList: toJS(cartMobx.info.freeTaxList)
      }
      console.log('🍄计算税和服务费 input', calcData)
      const feeResult = restaurantConfig.fee.calculate(calcData)
      // 简单的做一下认证
      if (feeResult && feeResult.gratuities && feeResult.taxes) {
        console.log('🍄计算税和服务费 output', feeResult)
        this.setFee(feeResult)
      } else {
        console.error('🍄计算税和服务费 error', feeResult)
      }
    }
  }

  /** 清空费用计算结果 */
  clearFee () {
    this.setFee({
      gratuities: [],
      gratuityTotal: 0,
      freeTaxTotal: 0,
      showTaxTotal: 0,
      taxTotal: 0,
      taxes: []
    })
  }

  init () {
    return FeeAPI.get().then((res: any) => {
      const { gratuity, tax, tip } = res || {}
      if (gratuity) {
        this.setGratuityConfig(gratuity)
      }
      if (tax) {
        // 转换fee配置到树形结构 cityCode -> diningOption -> productTaxCategory
        this.taxMapConfig = {}
        tax.forEach((tconfig: ITaxConfig) => {
          const cityCodeOption = tconfig.taxOptionsList.find(taxoption => taxoption.type === 'cityCode')
          const productTaxCategoryOption = tconfig.taxOptionsList.find(taxoption => taxoption.type === 'productTaxCategory')
          const diningOptionOption = tconfig.taxOptionsList.find(taxoption => taxoption.type === 'diningOption')
          if (cityCodeOption && productTaxCategoryOption && diningOptionOption) {
            merge(this.taxMapConfig, {
              [cityCodeOption.targetId]: {
                [diningOptionOption.targetId.toLowerCase()]: { // 主要解决后端存储eatType的dinein使用的是dineIn的问题
                  [productTaxCategoryOption.targetId]: Object.assign(pick(tconfig, 'name', 'rate', 'roundingOption', 'category', 'id'), {
                    taxCategoryId: Number(productTaxCategoryOption.targetId)
                  })
                }
              }
            })
          }
        })
      }
      return {
        gratuity: gratuity,
        tip: tip,
        taxConfig: this.taxMapConfig,
        cityId: storeMobx.info.cityId
      }
    })
  }

  get taxCategoryToTax () {
    if (storeMobx.info.cityId && this.taxMapConfig[storeMobx.info.cityId]) {
      if (cartMobx.info.eatType && this.taxMapConfig[storeMobx.info.cityId][cartMobx.info.eatType]) {
        return this.taxMapConfig[storeMobx.info.cityId][cartMobx.info.eatType]
      }
    }
    return {}
  }

  get taxCategoryResult () {
    return values(groupBy(this.calculatedTax, 'taxCategoryId')).map(items => {
      const taxCategoryId = items[0].taxCategoryId
      const rate = items[0].rate
      const name = items[0].taxName
      return {
        taxCategoryId,
        amount: sumBy(items, item => item.isTaxFree ? 0 : item.amount),
        name,
        rate,
        // isTaxFree: cartMobx.info.freeTaxList.includes(taxCategoryId),
        items
      }
    })
  }

  setTaxFee (pk?: string) {
    if (pk) {
      this.taxFreeAction[pk] = !this.taxFreeAction[pk]
    } else {
      this.taxFreeAction['-1'] = !this.taxFreeAction['-1']
    }
  }

  calTaxPrice (taxCategoryId: number, price: number) {
    const theTax = this.taxCategoryToTax[taxCategoryId.toString()]
    if (theTax) {
      return restaurantConfig.fee.calTaxByProduct(theTax, price)
    } else {
      return 0
    }
  }

  setGratuityConfig (gratuityConfig: IGratuityConfig) {
    this.gratuityConfig = {
      name: '',
      ...gratuityConfig
    }
  }
}
export default new Fee()
